/**
 * Types et interfaces pour le module Clients Admin
 * Source de vérité : customerOrgs (Firestore). Plus de lecture orders.
 * Période (7, 30, 90j) pour filtre inactifs et dérivation statut.
 */

import type { CustomerOrgView } from '@/services/firebase/customer-orgs-repository';

// ============================================
// PÉRIODE D'ANALYSE
// ============================================

/**
 * Périodes d'analyse disponibles
 */
export type AnalysisPeriod = 7 | 30 | 90;

export interface PeriodConfig {
    value: AnalysisPeriod;
    label: string;
    shortLabel: string;
}

export const PERIOD_OPTIONS: PeriodConfig[] = [
    { value: 7, label: '7 derniers jours', shortLabel: '7j' },
    { value: 30, label: '30 derniers jours', shortLabel: '30j' },
    { value: 90, label: '90 derniers jours', shortLabel: '90j' },
];

// ============================================
// STATUTS CLIENTS (basés sur période)
// ============================================

/**
 * Statut client basé sur la période sélectionnée
 * 
 * - new: Première commande dans la période
 * - existing: Première commande avant période + commande(s) dans période
 * - inactive: Aucune commande dans la période
 */
export type CustomerPeriodStatus = 'new' | 'existing' | 'inactive';

export interface CustomerStatusConfig {
    label: string;
    description: string;
    color: string;
    textClass: string;
    bgClass: string;
    borderClass: string;
}

export const CUSTOMER_STATUS_CONFIG: Record<CustomerPeriodStatus, CustomerStatusConfig> = {
    new: {
        label: 'Nouveau',
        description: 'Première commande dans la période',
        color: '#10B981',
        textClass: 'text-success',
        bgClass: 'bg-success-10',
        borderClass: 'border-success-20',
    },
    existing: {
        label: 'Existant actif',
        description: 'Client qui revient commander',
        color: '#3B82F6',
        textClass: 'text-primary',
        bgClass: 'bg-primary-10',
        borderClass: 'border-primary-20',
    },
    inactive: {
        label: 'Inactif',
        description: 'Aucune commande sur la période',
        color: '#9CA3AF',
        textClass: 'text-foreground-secondary',
        bgClass: 'bg-background-tertiary',
        borderClass: 'border-border',
    },
};

// ============================================
// DONNÉES CLIENT AGRÉGÉES
// ============================================

/**
 * Préférences produit d'un client
 */
export interface ProductPreference {
    reference: string;
    model?: string;
    count: number;
    totalAmount: number;
}

/**
 * Données agrégées d'un client
 * Inclut les stats totales ET les stats de la période sélectionnée
 */
export interface AggregatedCustomer {
    // Identité
    id: string; // email comme clé unique
    email: string;
    firstName?: string;
    lastName?: string;
    fullName: string;
    tiktokUsername?: string;
    phone?: string;

    // Stats TOTALES (historique complet)
    totalOrders: number;
    totalRevenue: number;
    averageCart: number;
    firstOrderDate: Date;
    lastOrderDate: Date;

    // Stats PÉRIODE (selon sélection)
    periodOrders: number;
    periodRevenue: number;
    periodAverageCart: number;

    // Statut et comportement
    periodStatus: CustomerPeriodStatus;
    purchaseFrequencyDays?: number; // Fréquence d'achat en jours (médiane)
    daysSinceLastOrder: number;

    // Produits préférés
    preferredProducts: ProductPreference[];

    // Références
    orderIds: string[];
}

// ============================================
// FILTRES ET TRI
// ============================================

export interface CustomerFilters {
    search: string;
    statuses: CustomerPeriodStatus[];
}

export type CustomerSortField =
    | 'fullName'
    | 'periodStatus'
    | 'firstOrderDate'
    | 'lastOrderDate'
    | 'periodOrders'
    | 'periodRevenue'
    | 'averageCart'
    | 'purchaseFrequencyDays';

export interface CustomerSort {
    field: CustomerSortField;
    direction: 'asc' | 'desc';
}

// ============================================
// STATISTIQUES GLOBALES
// ============================================

/**
 * Stats clients sur la période
 */
export interface CustomerStats {
    // Volumes clients
    totalCustomers: number;
    newCustomers: number;
    existingCustomers: number;
    inactiveCustomers: number;

    // Pourcentages
    newCustomersPercent: number;
    existingCustomersPercent: number;
    inactiveCustomersPercent: number;

    // CA par type
    totalRevenue: number;
    newCustomersRevenue: number;
    existingCustomersRevenue: number;

    // Pourcentages CA
    newRevenuePercent: number;
    existingRevenuePercent: number;

    // Commandes par type
    totalOrders: number;
    newCustomersOrders: number;
    existingCustomersOrders: number;

    // Métriques moyennes
    averageCart: number;
    averagePurchaseFrequency: number;
}

/**
 * Données pour graphiques d'évolution
 */
export interface CustomerTimeSeriesData {
    date: string; // Format YYYY-MM-DD
    newCustomers: number;
    existingCustomers: number;
    newRevenue: number;
    existingRevenue: number;
    newOrders: number;
    existingOrders: number;
}

// ============================================
// PROPS COMPOSANTS (customerOrgs)
// ============================================

export type CustomerOrgsSortField = 'lastPaidAt' | 'totalSpentCents';
export type CustomerOrgsFilter = 'all' | 'inactive';

export interface CustomersListProps {
    customers: CustomerOrgView[];
    loading: boolean;
    onSelectCustomer: (customer: CustomerOrgView) => void;
    selectedCustomerId?: string;
    period: AnalysisPeriod;
    sort: { field: CustomerOrgsSortField; direction: 'asc' | 'desc' };
    onSortChange: (field: CustomerOrgsSortField, direction: 'asc' | 'desc') => void;
    filter: CustomerOrgsFilter;
    onFilterChange: (f: CustomerOrgsFilter) => void;
    hasMore: boolean;
    onLoadMore: () => void;
    pageLimit: 25 | 50;
    onPageLimitChange?: (limit: 25 | 50) => void;
    search: string;
    onSearchChange: (s: string) => void;
    totalCount: number;
    inactiveCount: number;
    /** Pagination : page courante (1-based) et total de pages */
    currentPage?: number;
    totalPages?: number;
    onPageChange?: (page: number) => void;
}

export interface CustomerDetailProps {
    customer: CustomerOrgView | null;
    onClose: () => void;
    isOpen: boolean;
    period: AnalysisPeriod;
}

export interface CustomersDashboardProps {
    period?: AnalysisPeriod;
}
